/**
 * GSAP + ScrollTrigger Animations for Amascence
 * Luxury animation framework for high-end website experience
 */

// Wait for GSAP to load
document.addEventListener('DOMContentLoaded', function() {
  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded yet, retrying...');
    setTimeout(initAnimations, 100);
    return;
  }
  initAnimations();
});

function initAnimations() {
  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Set default ease for luxury feel - smooth and elegant
  gsap.defaults({
    ease: 'power2.out',
    duration: 1
  });

  // ============================================
  // PARALLAX OFFSET COMPENSATION
  // The floating text in product_video section has a ~200px parallax (y: 200 → 0).
  // This affects ScrollTrigger's position calculations for all sections below it,
  // causing triggers to fire ~240px too early. We compensate by adding this offset
  // to the 'start' position of all ScrollTrigger animations after product_video.
  // Affected sections: first_batch, fragrance_selector, testimonials, faq
  // ============================================
  var PARALLAX_OFFSET = 240;

  // ============================================
  // REMOVE data-animate FROM GSAP-MANAGED SECTIONS
  // Prevents theme CSS blockFadeIn from fighting GSAP opacity control.
  // ============================================
  document.querySelectorAll([
    '[id*="first_batch"] [data-animate]',
    '[data-wetheme-section-type="shoppable-videos"] [data-animate]',
    '[data-wetheme-section-type="accordion--faq"] [data-animate]'
  ].join(',')).forEach(function(el) {
    el.removeAttribute('data-animate');
  });

  // Also remove .show-on-scroll from GSAP-managed section wrappers
  // so the theme IntersectionObserver doesn't override GSAP opacity with fadeIn.
  document.querySelectorAll([
    '[data-wetheme-section-type="shoppable-videos"].show-on-scroll'
  ].join(',')).forEach(function(el) {
    el.classList.remove('show-on-scroll');
  });

  // ============================================
  // SMOOTH SCROLL - DISABLED (conflicts with parallax)
  // The fixed position + translateY approach conflicts with
  // individual element parallax transforms.
  // Re-enable later with proper ScrollTrigger scroller config if needed.
  // ============================================
  /*
  const smoothScroll = {
    current: 0,
    target: 0,
    ease: 0.075 // Lower = smoother, higher = snappier
  };

  // Only enable on desktop (touch devices have native smooth scroll)
  if (window.matchMedia('(min-width: 1024px)').matches && !('ontouchstart' in window)) {
    document.body.style.height = document.documentElement.scrollHeight + 'px';

    const smoothContent = document.querySelector('#MainContent') || document.querySelector('main') || document.body;

    if (smoothContent && smoothContent !== document.body) {
      smoothContent.style.position = 'fixed';
      smoothContent.style.top = '0';
      smoothContent.style.left = '0';
      smoothContent.style.width = '100%';
      smoothContent.style.willChange = 'transform';

      window.addEventListener('scroll', () => {
        smoothScroll.target = window.scrollY;
      });

      function smoothScrollRender() {
        smoothScroll.current += (smoothScroll.target - smoothScroll.current) * smoothScroll.ease;
        smoothContent.style.transform = `translateY(${-smoothScroll.current}px)`;
        ScrollTrigger.update();
        requestAnimationFrame(smoothScrollRender);
      }

      requestAnimationFrame(smoothScrollRender);
    }
  }
  */

  // ============================================
  // FADE UP ANIMATIONS
  // Add class "gsap-fade-up" to any element
  // ============================================
  gsap.utils.toArray('.gsap-fade-up').forEach(element => {
    gsap.from(element, {
      y: 60,
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // FADE IN ANIMATIONS
  // Add class "gsap-fade-in" to any element
  // ============================================
  gsap.utils.toArray('.gsap-fade-in').forEach(element => {
    gsap.from(element, {
      opacity: 0,
      duration: 1.2,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // STAGGER ANIMATIONS (for lists, grids)
  // Add class "gsap-stagger" to parent, "gsap-stagger-item" to children
  // ============================================
  gsap.utils.toArray('.gsap-stagger').forEach(container => {
    const items = container.querySelectorAll('.gsap-stagger-item');
    gsap.from(items, {
      y: 40,
      opacity: 0,
      duration: 0.8,
      stagger: 0.2,
      scrollTrigger: {
        trigger: container,
        start: 'top 70%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // PARALLAX EFFECT
  // Add class "gsap-parallax" and data-speed attribute
  // data-speed="0.5" = slower, data-speed="1.5" = faster
  // ============================================
  gsap.utils.toArray('.gsap-parallax').forEach(element => {
    const speed = element.dataset.speed || 0.5;
    gsap.to(element, {
      yPercent: -30 * speed,
      ease: 'none',
      scrollTrigger: {
        trigger: element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });

  // ============================================
  // TEXT PARALLAX (luxury layered text movement)
  // Add class "gsap-text-parallax" to container
  // Text elements move at different rates for depth
  // data-text-speed: base speed (default 0.3)
  // ============================================
  gsap.utils.toArray('.gsap-text-parallax').forEach(container => {
    const baseSpeed = parseFloat(container.dataset.textSpeed) || 0.3;

    // Headings move slower (float effect)
    const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(heading => {
      gsap.to(heading, {
        yPercent: -20 * baseSpeed,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5
        }
      });
    });

    // Body text moves slightly faster (creates separation)
    const bodyText = container.querySelectorAll('p, .rich-text__text, .rte');
    bodyText.forEach(text => {
      gsap.to(text, {
        yPercent: -35 * baseSpeed,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5
        }
      });
    });
  });

  // ============================================
  // TEXT REVEAL (character by character)
  // Add class "gsap-text-reveal" to text elements
  // ============================================
  gsap.utils.toArray('.gsap-text-reveal').forEach(element => {
    const text = element.textContent;
    element.innerHTML = '';

    // Wrap each character in a span
    text.split('').forEach(char => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      element.appendChild(span);
    });

    gsap.from(element.children, {
      opacity: 0,
      y: 20,
      duration: 0.5,
      stagger: 0.03,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // LINE REVEAL (for headings)
  // Add class "gsap-line-reveal" to heading elements
  // ============================================
  gsap.utils.toArray('.gsap-line-reveal').forEach(element => {
    // Create wrapper and mask
    const wrapper = document.createElement('div');
    wrapper.style.overflow = 'hidden';
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);

    gsap.from(element, {
      yPercent: 100,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: wrapper,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // IMAGE REVEAL (with mask)
  // Add class "gsap-image-reveal" to image containers
  // ============================================
  gsap.utils.toArray('.gsap-image-reveal').forEach(container => {
    const image = container.querySelector('img');
    if (!image) return;

    container.style.overflow = 'hidden';

    gsap.from(image, {
      scale: 1.3,
      duration: 1.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: container,
        start: 'top 70%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // SCALE UP ON SCROLL
  // Add class "gsap-scale-up" to any element
  // ============================================
  gsap.utils.toArray('.gsap-scale-up').forEach(element => {
    gsap.from(element, {
      scale: 0.8,
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // HORIZONTAL SLIDE
  // Add class "gsap-slide-left" or "gsap-slide-right"
  // ============================================
  gsap.utils.toArray('.gsap-slide-left').forEach(element => {
    gsap.from(element, {
      x: -100,
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  gsap.utils.toArray('.gsap-slide-right').forEach(element => {
    gsap.from(element, {
      x: 100,
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: element,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });
  });

  // ============================================
  // PINNED SECTION (stays fixed while scrolling)
  // Add class "gsap-pin" to section
  // ============================================
  gsap.utils.toArray('.gsap-pin').forEach(element => {
    ScrollTrigger.create({
      trigger: element,
      start: 'top top',
      end: '+=100%',
      pin: true,
      pinSpacing: true
    });
  });

  // ============================================
  // PROGRESS BAR ON SCROLL
  // Add class "gsap-progress" to a div (will fill width on scroll)
  // ============================================
  gsap.utils.toArray('.gsap-progress').forEach(element => {
    gsap.to(element, {
      width: '100%',
      ease: 'none',
      scrollTrigger: {
        trigger: document.body,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.3
      }
    });
  });

  // ============================================
  // AUTO-TARGET SECTIONS FOR ANIMATIONS
  // Automatically add animation classes to specific sections
  // ============================================

  // First Batch stagger (heading + paragraph)
  const firstBatchSection = document.querySelector('[id*="first_batch"]');
  if (firstBatchSection) {
    const firstBatchTargets = firstBatchSection.querySelectorAll('h2, .richtext-block');
    if (firstBatchTargets.length > 0) {
      gsap.set(firstBatchTargets, { opacity: 0, y: 25 });
      gsap.to(firstBatchTargets, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.2,
        scrollTrigger: {
          trigger: firstBatchSection,
          start: 'top 75%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // FAQ section stagger (GSAP) — trigger when section enters viewport
  const faqSection = document.querySelector('[data-wetheme-section-type="accordion--faq"]');
  if (faqSection) {
    faqSection.classList.remove('show-on-scroll');
    faqSection.classList.add('shown-on-scroll');

    const faqItems = faqSection.querySelectorAll('.accordion-content');
    if (faqItems.length > 0) {
      gsap.set(faqItems, { opacity: 0, y: 30 });
      gsap.to(faqItems, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.25,
        scrollTrigger: {
          trigger: faqSection,
          start: 'top 85%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // ============================================
  // SUBTLE PARALLAX FOR ALL SECTIONS
  // Direct element targeting (like meteor images which work)
  // ============================================

  // Product video container - directly target
  const productVideo = document.querySelector('.product-video-container');
  if (productVideo) {
    const productVideoSection = productVideo.closest('.product-video-section') || productVideo;
    const productVideoTargets = [productVideo];
    const productFloatText = productVideoSection.querySelector('[data-float-text]');
    if (productFloatText) {
      productVideoTargets.push(productFloatText);
    }

    gsap.set(productVideoTargets, { opacity: 0, y: 25 });
    gsap.to(productVideoTargets, {
      opacity: 1,
      y: 0,
      duration: 0.9,
      ease: 'power2.out',
      stagger: 0.2,
      scrollTrigger: {
        trigger: productVideoSection,
        start: 'top 75%',
        toggleActions: 'play none none none'
      }
    });

    gsap.to(productVideo, {
      y: -120,
      ease: 'none',
      scrollTrigger: {
        trigger: productVideo,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
    console.log('Parallax applied to Product Video');
  }


  // ============================================
  // STAGGER: SCENT INTRO TEXT
  // Lead, body paragraphs, and closing line fade up in sequence
  // ============================================
  var scentIntro = document.querySelector('.scent-intro');
  if (scentIntro) {
    var scentIntroItems = scentIntro.querySelectorAll('.scent-intro__lead, .scent-intro__body, .scent-intro__closing');
    if (scentIntroItems.length > 0) {
      gsap.set(scentIntroItems, { opacity: 0, y: 25 });
      gsap.to(scentIntroItems, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.25,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: scentIntro,
          start: 'top 75%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // Meteor features container
  const meteorFeatures = document.querySelector('.meteor-features');
  if (meteorFeatures) {
    gsap.to(meteorFeatures, {
      y: 80,
      ease: 'none',
      scrollTrigger: {
        trigger: meteorFeatures,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
    console.log('Parallax applied to Meteor Features');
  }

  // Waitlist email form section
  const waitlistForm = document.querySelector('#shopify-section-waitlist_form');
  if (waitlistForm) {
    gsap.to(waitlistForm, {
      y: -90,
      ease: 'none',
      scrollTrigger: {
        trigger: waitlistForm,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
    console.log('Parallax applied to Waitlist Form');
  }

  // FAQ section
  document.querySelectorAll('[data-wetheme-section-type="accordion--faq"]').forEach(section => {
    gsap.to(section, {
      y: 90,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
    console.log('Parallax applied to FAQ');
  });

  // First Batch section (whole section)
  const firstBatchParallax = document.querySelector('[id*="first_batch"]') ||
                             document.querySelector('[data-wetheme-section-id*="first_batch"]');
  if (firstBatchParallax) {
    gsap.to(firstBatchParallax, {
      y: 90,
      ease: 'none',
      scrollTrigger: {
        trigger: firstBatchParallax,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
    console.log('Parallax applied to First Batch section');
  }

  // ============================================
  // AUTO-TARGET PARALLAX ELEMENTS
  // Apply subtle parallax to meteor backgrounds
  // ============================================
  document.querySelectorAll('.meteor-features__bg').forEach(el => {
    gsap.to(el, {
      yPercent: -10,
      ease: 'none',
      scrollTrigger: {
        trigger: el.closest('.meteor-features__item') || el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });

  // ============================================
  // AUTO-TARGET STAGGER ANIMATIONS
  // Meteor features animate in sequence
  // ============================================
  const meteorGrid = document.querySelector('.meteor-features__grid');
  if (meteorGrid) {
    const meteorItems = meteorGrid.querySelectorAll('.meteor-features__item');
    if (meteorItems.length > 0) {
      gsap.from(meteorItems, {
        y: 50,
        opacity: 0,
        duration: 0.9,
        stagger: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: meteorGrid,
          start: 'top 70%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // ============================================
  // STAGGER: PRODUCT VIDEO FLOATING TEXT LINES
  // Two lines fade up in sequence when the text block enters view
  // Uses set()+to() pattern — gsap.from() can misfire with ScrollTrigger
  // ============================================
  var floatTextContainer = document.querySelector('[data-float-text]');
  if (floatTextContainer) {
    var floatLines = floatTextContainer.querySelectorAll('.product-video-float-line');
    if (floatLines.length > 0) {
      gsap.set(floatLines, { opacity: 0, y: 15 });
      gsap.to(floatLines, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.3,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: floatTextContainer.closest('.product-video-section') || floatTextContainer,
          start: 'top 75%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // ============================================
  // STAGGER: FIRST BATCH HEADING + TEXT
  // Heading fades up first, then body text follows
  // ============================================
  var firstBatchStagger = document.querySelector('[id*="first_batch"]');
  if (firstBatchStagger) {
    var fbHeading = firstBatchStagger.querySelector('h2');
    var fbBody = firstBatchStagger.querySelector('.richtext-block');
    var fbTargets = [];
    if (fbHeading) fbTargets.push(fbHeading);
    if (fbBody) fbTargets.push(fbBody);

    if (fbTargets.length > 0) {
      gsap.set(fbTargets, { opacity: 0, y: 20 });
      gsap.to(fbTargets, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: firstBatchStagger,
          start: 'top+=' + PARALLAX_OFFSET + 'px 75%',
          toggleActions: 'play none none none'
        }
      });
    }
  }

  // ============================================
  // STAGGER: FRAGRANCE SELECTOR NAMES + BOTTLE
  // Three names stagger top-to-bottom, bottle fades in with subtle scale
  // Suppress CSS transition on options so it doesn't fight GSAP
  // ============================================
  var fragranceSelector = document.querySelector('.fragrance-selector');
  if (fragranceSelector) {
    var fragranceLabelsContainer = fragranceSelector.querySelector('.fragrance-selector__left');
    var fragranceNames = fragranceSelector.querySelectorAll('.fragrance-selector__option');
    var fragranceBottle = fragranceSelector.querySelector('.fragrance-selector__video-mask');

    // Video/bottle fades in immediately when section enters view (independent of labels)
    if (fragranceBottle) {
      gsap.set(fragranceBottle, { opacity: 0, scale: 0.97 });
      gsap.to(fragranceBottle, {
        opacity: 1,
        scale: 1,
        duration: 0.9,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: fragranceSelector,
          start: 'top+=' + PARALLAX_OFFSET + 'px 75%',
          toggleActions: 'play none none none'
        }
      });
    }

    // Labels stagger when labels container comes into view
    if (fragranceNames.length > 0 && fragranceLabelsContainer) {
      // Disable CSS transition while GSAP controls opacity
      fragranceNames.forEach(function(el) { el.style.transition = 'none'; });

      gsap.set(fragranceNames, { opacity: 0, y: 15 });
      gsap.to(fragranceNames, {
        opacity: function(i, el) {
          return el.classList.contains('active') ? 1 : 0.4;
        },
        y: 0,
        duration: 0.7,
        stagger: 0.15,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: fragranceLabelsContainer,
          start: 'top+=' + PARALLAX_OFFSET + 'px 80%',
          toggleActions: 'play none none none'
        },
        onComplete: function() {
          // Re-enable CSS transitions after stagger finishes
          fragranceNames.forEach(function(el) {
            el.style.transition = '';
            el.style.opacity = '';
            el.style.transform = '';
          });
        }
      });
    }
  }

  // ============================================
  // HERO BRAND STACK TO SUBHEADING (Effectivity only)
  // AMA / SCE / NCE -> amascence
  // ============================================
  const heroBrand = document.querySelector('[data-hero-brand]');
  if (heroBrand) {
    const stack = heroBrand.querySelector('.hero-brand-anim__stack');
    const final = heroBrand.querySelector('.hero-brand-anim__final');
    const textInner = heroBrand.closest('.text-content--inner');
    const heroTitle = textInner ? textInner.querySelector('.hero-video--text-title') : null;
    const heroSubtitle = textInner ? textInner.querySelector('.hero-video--text-subtitle') : null;
    const heroScrollBtn = textInner ? textInner.querySelector('.scroll-down-btn') : null;
    const heroNoteWrap = textInner ? textInner.querySelector('.hero-batch-note-wrap') : null;
    const heroRectContent = textInner ? textInner.querySelector('.hero-rect-content') : null;

    // Build rectangle wrapper around title, subtitle, content, button, note
    var heroRect = null;
    if (textInner) {
      heroRect = document.createElement('div');
      heroRect.className = 'hero-text-rect';
      // Move title, subtitle, injected content, button, note into the rectangle
      if (heroTitle) heroRect.appendChild(heroTitle);
      if (heroSubtitle) heroRect.appendChild(heroSubtitle);
      if (heroRectContent) heroRect.appendChild(heroRectContent);
      if (heroScrollBtn) heroRect.appendChild(heroScrollBtn);
      if (heroNoteWrap) heroRect.appendChild(heroNoteWrap);
      textInner.appendChild(heroRect);
    }

    if (stack) {
      gsap.set(stack, { opacity: 0, scale: 1.05, filter: 'blur(6px)' });
    }
    if (final) {
      gsap.set(final, { opacity: 0, y: 8 });
    }

    // Hide title, subtitle, and rectangle — remove theme's built-in animation
    if (heroRect) {
      gsap.set(heroRect, { opacity: 0, scale: 0.97 });
    }
    if (heroTitle) {
      heroTitle.removeAttribute('data-animate');
      gsap.set(heroTitle, { opacity: 0, y: 20 });
    }
    if (heroSubtitle) {
      heroSubtitle.removeAttribute('data-animate');
      gsap.set(heroSubtitle, { opacity: 0, y: 20 });
    }
    if (heroNoteWrap) {
      gsap.set(heroNoteWrap, { opacity: 0, y: 20 });
    }
    if (heroRectContent) {
      gsap.set(heroRectContent, { opacity: 0, y: 20 });
    }

    const tl = gsap.timeline({ delay: 0 });

    // Phase 1: All three lines snap in together — fast blur-to-sharp
    if (stack) {
      tl.to(stack, {
        opacity: 0.65,
        scale: 1,
        filter: 'blur(0px)',
        duration: 0.4,
        ease: 'expo.out'
      });
    }
    // Phase 2: Hold so the viewer can read the stack
    // Phase 3: Graceful crossfade — stack fades out softly
    if (stack) {
      tl.to(stack, {
        opacity: 0,
        y: -10,
        duration: 0.9,
        ease: 'power3.inOut'
      }, '+=0.2');
    }
    // Phase 3b: Final word fades in with letter-spacing settle
    if (final) {
      tl.to(final, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: 'power3.inOut'
      }, '<');
    }
    // Phase 3c: Collapse container height to fit final text
    tl.to(heroBrand, {
      minHeight: 0,
      duration: 0.9,
      ease: 'power3.inOut'
    }, '<');

    // Phase 3d: Rectangle fades in
    if (heroRect) {
      tl.to(heroRect, {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.2');
    }

    // Phase 4: Title flows in inside the rectangle
    if (heroTitle) {
      tl.to(heroTitle, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'expo.out'
      }, '-=0.3');
    }

    // Phase 5: Subtitle flows in
    if (heroSubtitle) {
      tl.to(heroSubtitle, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'expo.out'
      }, '-=0.4');
    }

    // Phase 5b: Injected rect content flows in
    if (heroRectContent) {
      tl.to(heroRectContent, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'expo.out'
      }, '-=0.4');
    }

    // Phase 6: Scroll-down button appears after caption
    if (heroScrollBtn) {
      gsap.set(heroScrollBtn, { opacity: 0, y: -20 });
      tl.to(heroScrollBtn, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out'
      }, '-=0.2');
    }

    // Phase 7: Batch note appears after scroll button
    if (heroNoteWrap) {
      tl.to(heroNoteWrap, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.3');
    }

    // Scroll parallax — logo drifts independently, rectangle scrolls as one unit
    const heroSection = heroBrand.closest('[data-wetheme-section-type="video--video-hero"]');
    if (heroSection) {
      // Logo keeps its own parallax
      gsap.to(heroBrand, {
        y: -40,
        ease: 'none',
        scrollTrigger: {
          trigger: heroSection,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
      // Rectangle moves as a single block
      if (heroRect) {
        gsap.to(heroRect, {
          y: -60,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }
    }
  }

  // Refresh ScrollTrigger on page load complete
  window.addEventListener('load', () => {
    // ============================================
    // STAGGER: TESTIMONIALS VIDEO CARDS
    // Wait for Swiper to initialize before creating ScrollTrigger
    // ============================================
    var testimonialsSection = document.querySelector('[data-wetheme-section-type="shoppable-videos"]');
    if (testimonialsSection) {
      var testimonialSwiperContainer = testimonialsSection.querySelector('.swiper');

      function initTestimonialsStagger() {
        var testimonialHeaderTargets = testimonialsSection.querySelectorAll('.section-title--wrapper .section-title, .section-title--wrapper .section-description');
        var testimonialSlides = testimonialsSection.querySelectorAll('.shoppable-videos__slide');

        if (testimonialHeaderTargets.length > 0) {
          gsap.set(testimonialHeaderTargets, { opacity: 0, y: 20 });
          gsap.to(testimonialHeaderTargets, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: testimonialsSection,
              start: 'top+=' + PARALLAX_OFFSET + 'px 75%',
              toggleActions: 'play none none none'
            }
          });
        }
        if (testimonialSlides.length > 0) {
          gsap.set(testimonialSlides, { opacity: 0, y: 40 });

          var triggerEl = testimonialSwiperContainer || testimonialsSection;

          gsap.to(testimonialSlides, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: triggerEl,
              start: 'top+=' + PARALLAX_OFFSET + 'px 60%',
              toggleActions: 'play none none none'
            }
          });
        }
        ScrollTrigger.refresh();
      }

      // Wait for Swiper to initialize (has swiper-initialized class)
      if (testimonialSwiperContainer && testimonialSwiperContainer.classList.contains('swiper-initialized')) {
        initTestimonialsStagger();
      } else {
        // Poll for Swiper initialization
        var checkSwiper = setInterval(function() {
          if (testimonialSwiperContainer && testimonialSwiperContainer.classList.contains('swiper-initialized')) {
            clearInterval(checkSwiper);
            initTestimonialsStagger();
          }
        }, 50);
        // Fallback: stop polling after 3 seconds and init anyway
        setTimeout(function() {
          clearInterval(checkSwiper);
          if (!testimonialSwiperContainer || !testimonialSwiperContainer.classList.contains('swiper-initialized')) {
            initTestimonialsStagger();
          }
        }, 3000);
      }
    } else {
      ScrollTrigger.refresh();
    }
  });

  console.log('Amascence GSAP animations initialized');
}
